import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { of, Subscription, switchMap } from 'rxjs';
import { Question } from '../../models/question';
import { UserTagsResponse } from '../../models/userTagResponse';
import { Router } from '@angular/router';
import { LoggingService } from '../../services/logging.service';
import { UserTagCrudService } from '../../services/user-tag-crud.service';
import { AnswerService } from '../../services/answer.service';
import { AnswersWithUserId } from '../../models/answersWithUserId';
import { Answer } from '../../models/answer';
import { GetAllTagsRequest } from '../../models/getAllTagsRequest';
import { AnswerResponse } from '../../models/answerResponse';
import { TenantRouterService } from '../../services/tenant-router.service';
import { AuthService } from '../../services/auth.service';
import { Assignment } from '../../models/assignment';
import { ApiService } from '../../services/api.service';

@Component({
  selector: 'app-take-assessment',
  standalone: false,
  templateUrl: './take-assessment.component.html',
  styleUrl: './take-assessment.component.scss'
})
export class TakeAssessmentComponent implements OnInit, OnDestroy {

  userId !: number;
  assignmentId !: number;
  questionListSubscription: Subscription = new Subscription();
  questions: Question[] = [];
  userTags !: UserTagsResponse[];
  answerSubscription: Subscription = new Subscription();
  //selectedAnswers: (number | undefined)[] = [];
  selectedAnswers: number[] = [];

  previousAnswerSubscription: Subscription = new Subscription();
  message: string = '';
  showSavedMessage: boolean = false;
  isSaved !: boolean;

  remainingTimeSeconds: number = 0;     
  formattedTime: string = '00:00';    
  timerInterval: any;
  totalAssignmentMinutes: number =0;                 


  //single question navigation//
  currentQuestionIndex: number = 0;
  inactivityTimeout: any;
  activityEvents = ['mousemove', 'click', 'scroll', 'keypress', 'touchend', 'touchmove'];
  boundReset = () => {
    this.resetInactivityTimer();
  }
  myTimer: any;
  remainingTimeSyncIntervalSeconds:number =5;

  constructor(private userTagCrudService: UserTagCrudService,
    private router: Router, private loggingService: LoggingService,
    private answerService: AnswerService, private tenantRouter: TenantRouterService,
    private authService: AuthService,
    private apiService : ApiService,
  ) { }

  ngOnInit(): void {
    this.initInactivityTimer();
    this.isSaved = true;
    this.myTimer = setInterval(() => {
      console.log("isSaved:::", this.isSaved);
      if (!this.isSaved) {
        this.saveProgress();
      } else if (this.selectedAnswers.length == 0) {
        alert('No answers were selected yet.Please select an answer to save progress')
      } else {
        this.loggingService.logInfo('All answers are already saved.');
      }
    }, 60 * 1000);

    this.loggingService.logDebug("Getting the userId from session storage");
    const userid = sessionStorage.getItem('user_id');
    if (userid) {
      this.userId = parseInt(userid, 10);
    }
    this.loggingService.logDebug("Getting the assignmentId from session storage");
    const assignmentID = sessionStorage.getItem('assignment_id');
    if (assignmentID) {
      this.assignmentId = parseInt(assignmentID, 10);
    }
    let getAllTagsRequest: GetAllTagsRequest = new GetAllTagsRequest();
    getAllTagsRequest.userId = this.userId;
    getAllTagsRequest.assignmentId = this.assignmentId;
    this.loggingService.logDebug("Getting all questions assigned to user id " + this.userId);
    //Using switchMap
    this.questionListSubscription = this.userTagCrudService.getAllQuestion(getAllTagsRequest).pipe(
      switchMap(userTag=>{
        if (!userTag || !userTag.tags || userTag.tags.length === 0) {
          this.loggingService.logDebug("No tags found. Probably no assignment for this user.");
          this.message = "No assessment available for your account.";
          return of(null);
        }
        userTag.tags.forEach((tag) => {
          tag.questionGroups.forEach((questionGroup) => {
            questionGroup.questions.forEach((question) => {
              this.questions.push(question);
              this.shuffleArray(question.options);
            });
          });
        });
        this.shuffleArray(this.questions);
        return this.userTagCrudService.getAssignmentByAssignmentId(this.assignmentId);
      }),
      switchMap((assignmentResponse: any)=>{
        if (!assignmentResponse) return of(null); 

        const assignment = Array.isArray(assignmentResponse) ? assignmentResponse[0] : assignmentResponse;
        this.totalAssignmentMinutes = assignment.time *60 ;
        this.remainingTimeSeconds = assignment.remainingTime;

        return this.answerService.getAllAnswers(this.userId, this.assignmentId);
      })
    ).subscribe({
      next: (answers) => {
        if (!answers) return; // Handle early empty state
        if (answers && answers.length > 0) {
          // Restore selected answers
          this.selectedAnswers = [];
          this.questions.forEach((question, index) => {
            let matchedAnswers = answers.find(answer => answer.question?.questionId === question.questionId);
            if (matchedAnswers) {
              this.selectedAnswers[index] = matchedAnswers.questionOption.questionOptionId;
            }
          });
          // Resume timer from DB remaining seconds if present, else use total seconds
          if (this.remainingTimeSeconds > 0) {
            this.loggingService.logDebug(`Resuming timer from DB: ${this.remainingTimeSeconds}s`);
            this.startAssessmentTimer(this.remainingTimeSeconds);
          } else {
            this.loggingService.logDebug(`Starting timer from total duration: ${this.totalAssignmentMinutes}s`);
            this.startAssessmentTimer(this.totalAssignmentMinutes);
          }
        } else {
          // New attempt
          this.selectedAnswers = [];
          this.loggingService.logDebug(`New attempt, starting timer from total duration: ${this.totalAssignmentMinutes}s`);
          this.startAssessmentTimer(this.totalAssignmentMinutes);
        }
      },
      error: (err) => {
        console.error("Error in assessment data stream:", err);
        this.message = "No assessment available for your account.";
        this.questions = [];
      }
    });
  }

  // Helper function to shuffle any array in place
  shuffleArray(array: any[]): any[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]]; // Swaps elements
    }
    return array;
  }

  initInactivityTimer() {
    this.activityEvents.forEach(event => {
      window.addEventListener(event, this.boundReset)
    })
    this.resetInactivityTimer();
  }

  resetInactivityTimer() {
    clearTimeout(this.inactivityTimeout);
    this.inactivityTimeout = setTimeout(() => {
      this.handleSessionTimeout();
    },
      10 * 60 * 1000);
  }

  handleSessionTimeout() {
    this.removeInactivityListeners();
    alert('Your session has expired due to inactivity. We will now save your progress and log you out.');
    this.saveProgress(true);
  }

  //navigation methods//  
  nextQuestion(): void {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.currentQuestionIndex++;
    }
  }

  previousQuestion(): void {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    }
  }

  submitAnswers() {
    // Check if current question is answered before submitting

    if (!this.selectedAnswers[this.currentQuestionIndex]) {
      alert('Please select an answer before submitting.');
      return;
    }

    const answersWithUserId: AnswersWithUserId = new AnswersWithUserId();
    const answers: Answer[] = [];
    this.questions.forEach((question, index) => {
      const answer: Answer = new Answer();
      answer.questionId = question.questionId;
      answer.questionOptionId = Number(this.selectedAnswers[index]);
      answers.push(answer);
    });
    answersWithUserId.userId = this.userId;
    answersWithUserId.answers = answers;
    answersWithUserId.assignmentId = this.assignmentId;
    this.answerSubscription = this.answerService.createAnswers(answersWithUserId).subscribe(
      (response) => {
        this.clearTimerStorage();
        this.tenantRouter.navigate('thankyou');
        this.loggingService.logDebug("Answer submitted successfully and redirecting to thank you page");
      });
  }

  saveProgress(isTimeout: boolean = false) {
    const answersWithUserId: AnswersWithUserId = new AnswersWithUserId();
    const answers: Answer[] = [];

    this.questions.forEach((question, index) => {
      const selected = this.selectedAnswers[index];
      if (selected !== undefined && selected !== null) {
        const answer: Answer = new Answer();
        answer.questionId = question.questionId;
        answer.questionOptionId = Number(selected);
        answers.push(answer);
      }
    });
    if (answers.length > 0) {
      answersWithUserId.userId = this.userId;
      answersWithUserId.answers = answers;
      answersWithUserId.assignmentId = this.assignmentId;


      this.answerSubscription = this.answerService.saveAnswersProgress(answersWithUserId).subscribe({
        next: () => {
          // Positive case (Success)
          this.isSaved = true;
          this.message = 'Answer saved successfully';
          this.showSavedMessage = true;
          setTimeout(() => {
            this.showSavedMessage = false;
            this.message = '';
          }, 1500);
          this.loggingService.logDebug("Answer saved successfully");
          if (isTimeout) {
            this.authService.logout();
            this.tenantRouter.logoutRedirect();
          }
        },
        error: (err) => {
          // Negative case (Network/API Error)
          console.error("Error saving progress:", err);
          alert("Could not save your progress due to a network connection error. Please try again.");
          if (isTimeout) {
            this.authService.logout();
            this.tenantRouter.logoutRedirect();
          }
        }
      });
    } else {
      // Negative case: The user clicked save but hasn't chosen any options
      alert("Please select at least one answer before saving progress.");
      if (isTimeout) {
        this.authService.logout();
        this.tenantRouter.logoutRedirect();
      }
    }
  }

  exit() {
    const answersWithUserId: AnswersWithUserId = new AnswersWithUserId();
    const answers: Answer[] = [];
    this.stopTimer();
    this.persistRemainingTimeToServer();
    this.questions.forEach((question, index) => {
      const answer: Answer = new Answer();
      answer.questionId = question.questionId;
      answer.questionOptionId = Number(this.selectedAnswers[index]);
      if (this.selectedAnswers[index] !== undefined) {
        answers.push(answer);
      }
    });
    if (answers.length !== 0) {
      answersWithUserId.userId = this.userId;
      answersWithUserId.answers = answers;
      answersWithUserId.assignmentId = this.assignmentId;
      this.answerSubscription = this.answerService.saveAnswersProgress(answersWithUserId).subscribe(
        (response) => {
          this.tenantRouter.navigate('user-dashboard');
          this.loggingService.logDebug("Answer saved successfully and redirecting to user dashboard");
        });
    }
    else {
      this.tenantRouter.navigate('user-dashboard');
      alert('Exiting assessment without saving - no answers provided');
      this.loggingService.logDebug("Exiting assessment without saving - no answers provided");
    }
  }

  startAssessmentTimer(durationInSeconds: number): void {
      this.remainingTimeSeconds = durationInSeconds;
      if (this.remainingTimeSeconds <= 0) {
        this.remainingTimeSeconds = 0;
        this.updateFormattedTime();
        alert('Time has expired for this assessment!');
        this.submitAnswers();
        this.exit();
        return;
      }
      this.updateFormattedTime();
      this.stopTimer(); 
      this.timerInterval = setInterval(() => {
        if (this.remainingTimeSeconds > 0) {
          this.remainingTimeSeconds--;
          this.updateFormattedTime();
          if (this.remainingTimeSeconds % this.remainingTimeSyncIntervalSeconds === 0) {
            this.persistRemainingTimeToServer();
          }
        } else {
          this.stopTimer();
          this.clearTimerStorage();
          alert('Time is up! Your assessment will now be submitted.');
          this.submitAnswers();
          this.exit();
        }
      }, 1000);
    }

    private persistRemainingTimeToServer(): void {
      if (!this.assignmentId || this.remainingTimeSeconds <= 0) {
        return;
      }
      this.userTagCrudService.updateAssignmentRemainingTime(this.assignmentId, this.remainingTimeSeconds).subscribe({
        next: () => this.loggingService.logDebug(`Periodic auto-save of remaining time: ${this.remainingTimeSeconds}s`),
        error: (err) => console.error('Periodic auto-save of remaining time failed:', err)
      });
    }
    
  updateFormattedTime(): void {
    const minutes: number = Math.floor(this.remainingTimeSeconds / 60);
    const seconds: number = this.remainingTimeSeconds % 60;
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(seconds).padStart(2, '0');
    this.formattedTime = `${paddedMinutes}:${paddedSeconds}`;
  }

  clearTimerStorage(): void {
    if (this.assignmentId) {
        localStorage.removeItem(`start_time_${this.assignmentId}`);
    }
  }

  removeInactivityListeners() {
    this.activityEvents.forEach(event => {
      window.removeEventListener(event, this.boundReset);
    });
    clearTimeout(this.inactivityTimeout);
  }


  ngOnDestroy(): void {
    this.questionListSubscription.unsubscribe();
    this.answerSubscription.unsubscribe();
    this.previousAnswerSubscription.unsubscribe();
    this.removeInactivityListeners();
    clearInterval(this.myTimer);
    this.stopTimer(); 
  }

  stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  // clearAnswer(index: number): void {
  //this.selectedAnswers[index] = undefined;   // reset selection
  //}


}
